import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { FileText, Download, Send, Check, Loader2, AlertTriangle } from 'lucide-react';

const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';
const MOIS_NOMS = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

const TYPE_LABELS = {
  dmo: { label: 'DMO', dest: 'CPS', color: '#2D5A5A', desc: 'Déclaration de salaires et mouvements du personnel' },
  cst_4010: { label: 'CST 4010', dest: 'DICP', color: '#E8A020', desc: 'Contribution de Solidarite Territoriale' },
  annexe_4010a: { label: 'Annexe 4010-A', dest: 'DICP', color: '#6366f1', desc: 'Détail individuel CST par salarié' },
};

export default function DeclarationsPage() {
  const { mpFetch, can } = useMoanaPaye();
  const [entreprises, setEntreprises] = useState([]);
  const [selectedEnt, setSelectedEnt] = useState('');
  const [declarations, setDeclarations] = useState([]);
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(2026);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  useEffect(() => {
    mpFetch('/api/moana-paye/entreprises').then(d => { setEntreprises(d); if (d.length) setSelectedEnt(d[0].id); }).catch(console.error);
  }, []);

  const loadDecls = () => {
    if (selectedEnt) mpFetch(`/api/moana-paye/declarations/${selectedEnt}`).then(setDeclarations).catch(console.error);
  };
  useEffect(loadDecls, [selectedEnt]);

  const generer = async () => {
    setGenerating(true);
    setGenResult(null);
    try {
      const res = await mpFetch(`/api/moana-paye/declarations/${selectedEnt}/generer/${annee}/${mois}`, { method: 'POST' });
      setGenResult(res);
      loadDecls();
    } catch (err) { alert(err.message); } finally { setGenerating(false); }
  };

  const marquerTransmise = async (did) => {
    try {
      await mpFetch(`/api/moana-paye/declarations/${did}/transmettre`, { method: 'PUT', body: JSON.stringify({}) });
      loadDecls();
    } catch (err) { alert(err.message); }
  };

  // Grouper par mois/annee
  const grouped = {};
  declarations.forEach(d => {
    const key = `${d.annee}-${d.mois}`;
    if (!grouped[key]) grouped[key] = { mois: d.mois, annee: d.annee, items: [] };
    grouped[key].items.push(d);
  });
  const periods = Object.values(grouped).sort((a, b) => b.annee - a.annee || b.mois - a.mois);

  return (
    <div data-testid="mp-declarations">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Declarations sociales & fiscales</h2>
      </div>

      {/* Generer */}
      {can('generer_declarations') && (
      <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 mb-6">
        <h3 className="text-white font-semibold text-sm mb-3">Generer les declarations</h3>
        <p className="text-slate-500 text-xs mb-4">Genere automatiquement les 3 formulaires officiels a partir des bulletins de paie clotures.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={mois} onChange={e => setMois(parseInt(e.target.value))} className="bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            {MOIS_NOMS.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
          </select>
          <input type="number" value={annee} onChange={e => setAnnee(parseInt(e.target.value))} className="bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm w-20" />
          <button onClick={generer} disabled={generating} className="flex items-center gap-2 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50" data-testid="mp-decl-generer">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {generating ? 'Génération...' : 'Générer DMO + CST + Annexe'}
          </button>
        </div>
        {genResult && (
          <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <p className="text-green-400 text-sm font-medium mb-2">{genResult.message}</p>
            <div className="flex gap-3 flex-wrap">
              {genResult.declarations?.map(d => {
                const info = TYPE_LABELS[d.type.toLowerCase().replace(' ', '_').replace('annexe 4010-a', 'annexe_4010a').replace('cst 4010', 'cst_4010')] || TYPE_LABELS.dmo;
                return (
                  <div key={d.id} className="bg-white/5 rounded-lg px-3 py-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: info?.color || '#2D5A5A' }} />
                    <span className="text-white text-xs font-medium">{d.type}</span>
                    {d.nb_salaries != null && <span className="text-slate-500 text-xs">{d.nb_salaries} sal.</span>}
                    {d.total_cst != null && <span className="text-[#E8A020] text-xs">{fmt(d.total_cst)} F</span>}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/moana-paye/declarations/pdf/${d.id}`} target="_blank" rel="noreferrer" className="text-[#2D5A5A] hover:text-[#E8A020]"><Download className="w-3.5 h-3.5" /></a>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Historique */}
      <h3 className="text-white font-semibold text-sm mb-3">Historique des declarations</h3>
      {periods.length === 0 ? (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl p-8 text-center">
          <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">Aucune declaration generee</p>
        </div>
      ) : (
        <div className="space-y-4">
          {periods.map(period => (
            <div key={`${period.annee}-${period.mois}`} className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-white/3 border-b border-white/10">
                <span className="text-white font-medium text-sm">{MOIS_NOMS[period.mois]} {period.annee}</span>
              </div>
              <div className="divide-y divide-white/5">
                {period.items.map(d => {
                  const info = TYPE_LABELS[d.type_declaration] || {};
                  return (
                    <div key={d.id} className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: (info.color || '#666') + '20' }}>
                          <FileText className="w-4 h-4" style={{ color: info.color || '#666' }} />
                        </div>
                        <div>
                          <div className="text-white text-sm font-medium">{info.label || d.type_declaration}</div>
                          <div className="text-slate-500 text-xs">{info.desc || ''} — {info.dest || ''}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {d.metadata?.total_cst != null && <span className="text-[#E8A020] text-xs font-medium">{fmt(d.metadata.total_cst)} F</span>}
                        {d.metadata?.nb_salaries != null && <span className="text-slate-500 text-xs">{d.metadata.nb_salaries} sal.</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          d.statut === 'transmise' ? 'bg-green-500/15 text-green-400' : 'bg-[#E8A020]/15 text-[#E8A020]'
                        }`}>{d.statut === 'transmise' ? 'Transmise' : 'Generee'}</span>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/moana-paye/declarations/pdf/${d.id}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" title="Telecharger PDF"><Download className="w-4 h-4" /></a>
                        {d.statut !== 'transmise' && (
                          <button onClick={() => marquerTransmise(d.id)} className="text-slate-400 hover:text-green-400" title="Marquer transmise"><Send className="w-4 h-4" /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
